import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, ClipboardDocumentListIcon, UserIcon } from '@heroicons/react/24/outline';
import { enrollmentAPI, studentAPI, batchAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import { useDebounce } from '../../hooks/useDebounce';
import Toast from '../Toast';

const Enrollments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const searchInputRef = useRef(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const queryClient = useQueryClient();

  // Debounce the search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Track cursor position and focus state
  const [cursorPosition, setCursorPosition] = useState(0);
  const [shouldMaintainFocus, setShouldMaintainFocus] = useState(false);

  // Maintain focus when search results update
  useEffect(() => {
    if (shouldMaintainFocus && searchInputRef.current) {
      // Use a small delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          const position = Math.min(cursorPosition, searchTerm.length);
          searchInputRef.current.setSelectionRange(position, position);
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [debouncedSearchTerm, shouldMaintainFocus, cursorPosition, searchTerm.length]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCursorPosition(e.target.selectionStart);
    setShouldMaintainFocus(true);
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShouldMaintainFocus(true);
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    setShouldMaintainFocus(false);
  };

  const { data: enrollments, isLoading: enrollmentsLoading, error: enrollmentsError } = useQuery({
    queryKey: ['enrollments', currentPage, debouncedSearchTerm],
    queryFn: () => {
      if (debouncedSearchTerm) {
        return enrollmentAPI.search(debouncedSearchTerm).then(res => res.data);
      }
      return enrollmentAPI.getAll(currentPage * itemsPerPage, itemsPerPage, true).then(res => res.data);
    },
  });

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: totalEnrollmentsCount, isLoading: enrollmentsCountLoading } = useQuery({
    queryKey: ['enrollments-count'],
    queryFn: () => enrollmentAPI.getCount().then(res => res.data.count),
    enabled: !debouncedSearchTerm, // Only fetch total count when not searching
  });

  // Mark enrollment fee as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: (enrollmentId) => enrollmentAPI.markEnrollmentFeePaid(enrollmentId),
    onSuccess: () => {
      setToast({ show: true, message: 'Enrollment fee marked as paid successfully!', type: 'success' });
      // Invalidate and refetch enrollments data
      queryClient.invalidateQueries(['enrollments']);
      queryClient.invalidateQueries(['enrollments-count']);
    },
    onError: (error) => {
      setToast({ 
        show: true, 
        message: error.response?.data?.detail || 'Failed to mark enrollment fee as paid', 
        type: 'error' 
      });
    },
  });

  const handleMarkAsPaid = (enrollmentId) => {
    if (window.confirm('Are you sure you want to mark this enrollment fee as paid?')) {
      markAsPaidMutation.mutate(enrollmentId);
    }
  };

  const filteredEnrollments = enrollments || [];

  const isLoading = enrollmentsLoading || studentsLoading || batchesLoading || enrollmentsCountLoading;
  const error = enrollmentsError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading enrollments: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Enrollments</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Manage student enrollments</p>
        </div>
        <Link
          to="/enrollments/new"
          className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-800"
        >
          <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">New Enrollment</span>
          <span className="sm:hidden">New</span>
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
            placeholder="Search enrollments by student name, ID, or batch..."
          />
        </div>
      </div>

      {/* Enrollments List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-white">
            {debouncedSearchTerm 
              ? `${filteredEnrollments.length} Enrollment${filteredEnrollments.length !== 1 ? 's' : ''}`
              : `${totalEnrollmentsCount || 0} Enrollment${(totalEnrollmentsCount || 0) !== 1 ? 's' : ''}`
            }
          </h2>
        </div>
        
        {filteredEnrollments.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <ClipboardDocumentListIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-300">No enrollments found</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-400">
              {debouncedSearchTerm ? 'Try adjusting your search terms.' : 'Get started by creating a new enrollment.'}
            </p>
            {!debouncedSearchTerm && (
              <div className="mt-4 sm:mt-6">
                <Link
                  to="/enrollments/new"
                  className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  New Enrollment
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Batch
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Enrollment Fee
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filteredEnrollments.map((enrollment) => {
                  const student = students?.find(s => (s._id || s.id) === enrollment.student_id);
                  const batch = batches?.find(b => (b._id || b.id) === enrollment.batch_id);
                  const enrollmentId = enrollment._id || enrollment.id;
                  
                  return (
                    <tr key={enrollmentId} className="hover:bg-gray-700">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-purple-600 flex items-center justify-center">
                              <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-3 sm:ml-4">
                            <div className="text-sm font-medium text-white">
                              {student ? `${student.first_name} ${student.last_name}` : 'Unknown Student'}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-400">
                              ID: {student?.student_id || 'N/A'}
                            </div>
                            {/* Mobile-only batch info */}
                            <div className="sm:hidden text-xs text-gray-400 mt-1">
                              {batch?.batch_name || 'Unknown Batch'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                        <div>{batch?.batch_name || 'Unknown Batch'}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                        <div>{formatIndianCurrency(enrollment.enrollment_fee_amount || 0)}</div>
                        <div className="text-xs text-gray-400">
                          {enrollment.enrollment_fee_paid ? 'Paid' : 'Pending'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          enrollment.enrollment_fee_paid
                            ? 'bg-green-900/30 text-green-300 border border-green-700'
                            : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
                        }`}>
                          {enrollment.enrollment_fee_paid ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {!enrollment.enrollment_fee_paid && enrollment.enrollment_fee_amount > 0 && (
                            <button
                              onClick={() => handleMarkAsPaid(enrollmentId)}
                              disabled={markAsPaidMutation.isPending}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                              title="Mark as Paid"
                            >
                              Mark as Paid
                            </button>
                          )}
                          {enrollment.enrollment_fee_paid && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-300 bg-green-900/30 border border-green-700 rounded-md">
                              <span className="mr-1 font-bold">₹</span>
                              Paid
                            </span>
                          )}
                          {(!enrollment.enrollment_fee_amount || enrollment.enrollment_fee_amount === 0) && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-400 bg-gray-700 border border-gray-600 rounded-md">
                              No Fee
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!debouncedSearchTerm && filteredEnrollments.length > 0 && (
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalEnrollmentsCount || 0)} of {totalEnrollmentsCount || 0} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={(currentPage + 1) * itemsPerPage >= (totalEnrollmentsCount || 0)}
                className="px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default Enrollments; 